import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart3, FileText, Inbox, CheckCircle } from 'lucide-react';

interface Props {
  companyId: string;
}

export function CompanyStatsCard({ companyId }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, open: 0, closed: 0, applications: 0 });

  useEffect(() => {
    fetchStats();
  }, [companyId]);

  const fetchStats = async () => {
    const { data: opps } = await supabase
      .from('opportunities')
      .select('id, status')
      .eq('company_id', companyId);

    if (!opps || opps.length === 0) {
      setStats({ total: 0, open: 0, closed: 0, applications: 0 });
      return;
    }

    const open = opps.filter(o => o.status === 'open').length;
    const closed = opps.filter(o => o.status === 'closed').length;

    const { count } = await supabase
      .from('applications')
      .select('*', { count: 'exact', head: true })
      .in('opportunity_id', opps.map(o => o.id));

    setStats({ total: opps.length, open, closed, applications: count || 0 });
  };

  const items = [
    { label: t('companyProfile.stats.totalOpportunities'), value: stats.total, icon: FileText },
    { label: t('companyProfile.stats.openOpportunities'), value: stats.open, icon: CheckCircle },
    { label: t('companyProfile.stats.closedOpportunities'), value: stats.closed, icon: BarChart3 },
    { label: t('companyProfile.stats.totalApplications'), value: stats.applications, icon: Inbox },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <BarChart3 className="h-5 w-5 text-primary" />
          {t('companyProfile.stats.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {items.map(item => (
            <div key={item.label} className="text-center">
              <item.icon className="mx-auto h-5 w-5 text-muted-foreground mb-1" />
              <p className="text-2xl font-bold">{item.value}</p>
              <p className="text-xs text-muted-foreground">{item.label}</p>
            </div>
          ))}
        </div>
        {stats.applications > 0 && (
          <Button variant="link" className="mt-3 p-0 h-auto" onClick={() => navigate('/dashboard/company/applications')}>
            {t('companyProfile.stats.viewApplications')} →
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
